import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Mail, Send, Loader2, Paperclip, Save, Eye, ChevronDown,
  FileText, AlertTriangle, CheckCircle, UserPlus, Clock, X
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { formatDate } from '../lib/dateUtils';

const CURRENCY = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const TEMPLATE_OPTIONS = [
  { id: 'invoice', label: 'Send Invoice', icon: FileText, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { id: 'reminder', label: 'Payment Reminder', icon: AlertTriangle, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'payment_received', label: 'Payment Received', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200' },
];

const ESTIMATE_TEMPLATE_OPTIONS = [
  { id: 'estimate', label: 'Send Estimate', icon: FileText, color: 'text-teal-600 bg-teal-50 border-teal-200' },
];

export default function SendInvoiceEmail({ isOpen, onClose, invoice, docType = 'invoice' }) {
  const [toEmail, setToEmail] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [templateType, setTemplateType] = useState('invoice');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saveEmail, setSaveEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [customBody, setCustomBody] = useState('');
  const [useCustomBody, setUseCustomBody] = useState(false);

  const contacts = invoice?.billing_contacts || [];
  const primaryContact = contacts.find(c => c.is_primary) || contacts[0];

  const loadPreview = useCallback(async (ttype) => {
    if (!invoice?._id) return;
    if (docType === 'estimate') {
      // No server-side preview for estimates yet — set a sane default subject.
      setSubject(`Estimate ${invoice.estimate_number || invoice.invoice_number || ''} from Half Eaten Donut`);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await adminApi.post('/api/admin/email/template-preview', {
        template_type: ttype,
        invoice_id: invoice._id,
        client_name: invoice.client_name,
      });
      setPreviewHtml(res.data.html);
      setSubject(res.data.subject);
    } catch {
      setSubject(`Invoice ${invoice?.invoice_number || ''} from Half Eaten Donut`);
    } finally {
      setLoadingPreview(false);
    }
  }, [invoice, docType]);

  useEffect(() => {
    if (invoice && isOpen) {
      const email = primaryContact?.email || invoice.client_email || '';
      setToEmail(email);
      setCcEmails('');
      setSaveEmail(false);
      setUseCustomBody(false);
      setCustomBody('');
      setShowPreview(false);

      // Auto-detect template: overdue -> reminder, paid -> payment_received, else invoice
      let autoTemplate = docType === 'estimate' ? 'estimate' : 'invoice';
      if (docType !== 'estimate') {
        if (invoice.status === 'overdue') autoTemplate = 'reminder';
        else if (invoice.status === 'paid' || (invoice.balance_due !== undefined && invoice.balance_due <= 0)) autoTemplate = 'payment_received';
      }
      setTemplateType(autoTemplate);
      loadPreview(autoTemplate);

      // Load email history for this doc (estimates use a separate query param)
      const historyQ = docType === 'estimate'
        ? `estimate_id=${invoice._id}`
        : `invoice_id=${invoice._id}`;
      adminApi.get(`/api/admin/email/logs?${historyQ}&limit=5`)
        .then(res => setEmailLogs(res.data.logs || []))
        .catch(() => setEmailLogs([]));
    }
  }, [invoice, isOpen, primaryContact?.email, loadPreview, docType]);

  const handleTemplateChange = (ttype) => {
    setTemplateType(ttype);
    loadPreview(ttype);
  };

  const handleSend = async () => {
    if (!toEmail.trim()) {
      toast.error('Please enter recipient email');
      return;
    }
    setSending(true);
    try {
      const ccList = ccEmails.split(',').map(e => e.trim()).filter(Boolean);
      if (docType === 'estimate') {
        await adminApi.post('/api/admin/email/send-estimate', {
          estimate_id: invoice._id,
          to_email: toEmail.trim(),
          cc_emails: ccList.length > 0 ? ccList : null,
          subject,
          body: useCustomBody ? customBody : null,
          save_email_to_client: saveEmail,
        });
        toast.success(`Estimate ${invoice.estimate_number || invoice.invoice_number || ''} sent to ${toEmail}`);
      } else {
        await adminApi.post('/api/admin/email/send-invoice', {
          invoice_id: invoice._id,
          to_email: toEmail.trim(),
          cc_emails: ccList.length > 0 ? ccList : null,
          subject,
          body: useCustomBody ? customBody : null,
          template_type: templateType,
          save_email_to_client: saveEmail,
        });
        toast.success(`Invoice ${invoice.invoice_number} sent to ${toEmail}`);
      }
      onClose(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send email';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => !sending && onClose(false)}>
      <DialogContent
        className="sm:max-w-[680px] bg-white max-h-[90vh] overflow-y-auto p-0"
        data-testid="send-invoice-email-dialog"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <div className="p-1.5 bg-indigo-50 rounded-xl">
                <Mail className="w-4 h-4 text-indigo-600" />
              </div>
              Email {docType === 'estimate' ? `Estimate ${invoice.estimate_number || invoice.invoice_number || ''}` : `Invoice ${invoice.invoice_number}`}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Template Picker */}
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 block">Template</label>
            <div className="flex gap-2">
              {(docType === 'estimate' ? ESTIMATE_TEMPLATE_OPTIONS : TEMPLATE_OPTIONS).map(t => {
                const Icon = t.icon;
                const active = templateType === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => handleTemplateChange(t.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      active ? t.color + ' ring-1 ring-offset-1' : 'text-neutral-500 bg-white/40 border-white/40 hover:bg-neutral-100'
                    }`}
                    data-testid={`template-${t.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/40 rounded-xl p-3 border">
              <div className="text-xs text-neutral-500 mb-0.5">Client</div>
              <div className="text-sm font-semibold text-[#1a1a2e] truncate">{invoice.client_name}</div>
            </div>
            <div className="bg-white/40 rounded-xl p-3 border">
              <div className="text-xs text-neutral-500 mb-0.5">Total</div>
              <div className="text-sm font-semibold text-[#1a1a2e]">{CURRENCY(invoice.total)}</div>
            </div>
            <div className="bg-white/40 rounded-xl p-3 border">
              <div className="text-xs text-neutral-500 mb-0.5">Balance Due</div>
              <div className={`text-sm font-semibold ${(invoice.balance_due ?? invoice.total) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {CURRENCY(invoice.balance_due ?? invoice.total)}
              </div>
            </div>
          </div>

          {/* To Email with Contact Dropdown */}
          <div className="relative">
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5 block">To</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="email"
                  placeholder="client@example.com"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  className="pr-8"
                  data-testid="email-to-input"
                />
                {contacts.length > 0 && (
                  <button
                    onClick={() => setShowContactDropdown(!showContactDropdown)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-neutral-100 rounded"
                    data-testid="contact-dropdown-btn"
                  >
                    <ChevronDown className="w-4 h-4 text-neutral-400" />
                  </button>
                )}
                {showContactDropdown && contacts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg py-1" data-testid="contact-dropdown">
                    {contacts.map((c, i) => (
                      <button
                        key={i}
                        onClick={() => { setToEmail(c.email); setShowContactDropdown(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-white/60 flex items-center justify-between"
                      >
                        <div>
                          <span className="text-sm font-medium text-[#1a1a2e]">{c.name || c.email}</span>
                          {c.role && <span className="text-xs text-neutral-400 ml-1.5">{c.role}</span>}
                          <div className="text-xs text-neutral-500">{c.email}</div>
                        </div>
                        {c.is_primary && (
                          <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">PRIMARY</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* CC */}
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5 block">CC</label>
            <Input
              placeholder="cc1@example.com, cc2@example.com"
              value={ccEmails}
              onChange={(e) => setCcEmails(e.target.value)}
              data-testid="email-cc-input"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5 block">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-subject-input"
            />
          </div>

          {/* Custom body toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Paperclip className="w-3.5 h-3.5 text-neutral-400" />
              <span className="text-xs text-neutral-500">{docType === 'estimate' ? `Estimate_${invoice.estimate_number || invoice.invoice_number || ''}.pdf` : `${invoice.invoice_number}.pdf`} will be attached</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                data-testid="preview-toggle-btn"
              >
                <Eye className="w-3.5 h-3.5" />
                {showPreview ? 'Hide' : 'Preview'}
              </button>
              <label className="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomBody}
                  onChange={(e) => setUseCustomBody(e.target.checked)}
                  className="rounded border-white/40 w-3.5 h-3.5"
                />
                Custom message
              </label>
            </div>
          </div>

          {/* Email Preview */}
          {showPreview && !useCustomBody && (
            <div className="border rounded-xl overflow-hidden" data-testid="email-preview">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-12 text-neutral-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading preview...
                </div>
              ) : (
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-[360px] border-0"
                  sandbox=""
                />
              )}
            </div>
          )}

          {/* Custom body textarea */}
          {useCustomBody && (
            <div>
              <textarea
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                placeholder="Write your custom HTML email body here..."
                data-testid="email-body-input"
              />
            </div>
          )}

          {/* Save email + Email History */}
          <div className="flex items-center justify-between">
            {!invoice.client_email && toEmail && (
              <label className="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer" data-testid="save-email-checkbox">
                <input
                  type="checkbox"
                  checked={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.checked)}
                  className="rounded border-white/40 w-3.5 h-3.5"
                />
                <Save className="w-3 h-3" /> Save email to client
              </label>
            )}
            <div />
          </div>

          {/* Email History */}
          {emailLogs.length > 0 && (
            <div className="bg-white/40 rounded-xl p-3 border" data-testid="email-history">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 mb-2">
                <Clock className="w-3 h-3" /> Email History
              </div>
              <div className="space-y-1.5">
                {emailLogs.map((log, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-neutral-600">
                      Sent to <span className="font-medium">{log.to_email}</span>
                    </span>
                    <span className="text-neutral-400">
                      {log.sent_at ? formatDate(log.sent_at) : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-white/40 flex justify-end gap-2 rounded-b-xl">
          <Button variant="outline" size="sm" onClick={() => onClose(false)} disabled={sending} data-testid="email-cancel-btn">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={sending || !toEmail.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="email-send-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending...</>
            ) : (
              <><Send className="w-4 h-4 mr-1.5" /> Send {(docType === 'estimate' ? ESTIMATE_TEMPLATE_OPTIONS : TEMPLATE_OPTIONS).find(t => t.id === templateType)?.label || 'Email'}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
